import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button, Card, Container, Table } from 'react-bootstrap';
import { Icon } from '@lomarengas/design-system';
import {
  DataTable,
  DataTableItem,
  Loader,
  PaginationComponent,
  SectionHeader,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  AddOnsQueryFilters,
  getAllAddOnsAsync,
  selectAddOns,
  selectAddOnsPagination,
  selectAddOnsLoadingStatus,
} from './addonSlice';
import { LOADING_STATUS } from '../../common/constants';
import AddOnDefinitionDetails from './AddOnDefinitionDetails';
import { InitialPaginationParameters } from '../../common/types/pagination.types';

function AddOns() {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const addonDefinitions = useAppSelector(selectAddOns);
  const addonsPagination = useAppSelector(selectAddOnsPagination);
  const addOnsLoadingStatus = useAppSelector(selectAddOnsLoadingStatus);
  const [isInitialState, setIsInitialState] = useState<boolean>(false);
  const [filtering, setFiltering] = useState<AddOnsQueryFilters>({
    culture: 'fi_FI',
    PageSize: InitialPaginationParameters.PageSize,
    PageNumber: InitialPaginationParameters.PageNumber,
  });

  // Similar to componentDidMount and componentDidUpdate:
  useEffect(() => {
    if (!isInitialState) {
      setIsInitialState(!isInitialState);
      dispatch(getAllAddOnsAsync(filtering));
    }
  });

  const [addonsTableItems, setAddonTableItems] = useState<DataTableItem[]>([]);

  useEffect(() => {
    if (addonDefinitions && addonDefinitions.length > 0) {
      const formattedAddonsList: DataTableItem[] = [];
      addonDefinitions?.forEach(item => {
        formattedAddonsList.push({
          ItemId: item.id,
          Items: [item.name != null ? ` ${item.number} ${item.name}` : ''],
          Url: `/accommodations/addondefinitions/${item.id}`,
        });
      });
      setAddonTableItems(formattedAddonsList);
    }
  }, [addonDefinitions]);

  // gets translated item based on keyId and locale
  // this is used in html to fetch proper translation of text
  function getItem(locale, keyId, item) {
    if (addOnsLoadingStatus === LOADING_STATUS.IDLE) {
      const found = item.translations.filter(
        a => a.locale === locale && a.keyId === keyId,
      );
      if (found && found[0] && found[0].text) {
        return found[0].text;
      }
    }
    return '';
  }

  // calls actual search, which calls internal addOns Async
  // and that is then routed to backend
  const doSearch = () => {
    dispatch(getAllAddOnsAsync(filtering));
  };

  useEffect(() => {
    doSearch();
  }, []);

  const HandlePaginationClick = (Page: number) => {
    const paginatedFiltering = filtering;
    paginatedFiltering.PageNumber = Page;
    setFiltering(paginatedFiltering);
    dispatch(getAllAddOnsAsync(paginatedFiltering));
  };

  // Adds new Addon with empty dataset
  // and then we route to edit that data.
  const addNew = event => {
    navigate('/accommodations/addondefinitions/addnew?id=new');
    event.preventDefault();
  };

  const AddNewButton: JSX.Element = (
    <Button variant="blue" className="ps-16" onClick={addNew}>
      <Icon CssClass="me-8" AriaLabel="Uusi" Name="plus" />
      Lisää uusi
    </Button>
  );

  // draws elements to browser
  return (
    <>
      <Helmet>
        <title>Lisäpalvelut - Haku</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Majoitukset ja Ryhmät']}
          Children={AddNewButton}
          Title="Lisäpalvelut"
        />

        <Loader IsLoading={addOnsLoadingStatus === LOADING_STATUS.LOADING} />

        {addOnsLoadingStatus === LOADING_STATUS.IDLE &&
          addonDefinitions !== null &&
          addonDefinitions.length > 0 && (
            <>
              <DataTable
                Header={['Lisäpalvelun nimi']}
                Items={addonsTableItems}
                LegendText="lisäpalvelua"
                TotalItems={addonsPagination.totalCount}
              />
              <PaginationComponent
                OnPageClick={HandlePaginationClick}
                PaginationData={addonsPagination}
              />
            </>
          )}

        {addOnsLoadingStatus === LOADING_STATUS.IDLE &&
          addonDefinitions != null &&
          addonDefinitions.length < 1 && <p>Ei lisäpalveluita.</p>}
      </Container>
    </>
  );
}

export default AddOns;
